import React, { useContext, useEffect, useState } from "react";
import BaseWrapper from "../../components/ui/BaseWrapper";
import { styled } from "@mui/material/styles";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import PropTypes from "prop-types";
import { brandFontFamily, brandInputTextColor, brandPrimaryColor } from "../../utils/variables";
import { useLocation, useNavigate } from "react-router-dom";
import routes from "../../routes";
import { LayoutContext } from "../../context/LayoutContext";
import useAuth from "../../hooks/useAuth";
import CustomerSettingsHeader from "./components/CustomerSettingsHeader";

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`customer-settings-tabpanel-${index}`}
      aria-labelledby={`customer-settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `customer-settings-tab-${index}`,
    "aria-controls": `customer-settings-tabpanel-${index}`,
  };
}

const CustomTab = styled((props) => <Tab disableRipple {...props} />)(({ theme }) => ({
  textTransform: "none",
  minWidth: 0,
  [theme.breakpoints.up("sm")]: {
    minWidth: 0,
  },
  height: "3px",
  width: "230px",
  fontWeight: 300,
  fontSize: "16px",
  boxShadow: "0px -1px 8px 0px rgba(0, 0, 0, 0.09)",
  borderRadius: "6px",
  margin: "13px 0",
  background: "white",
  marginRight: theme.spacing(1),
  color: brandInputTextColor,
  fontFamily: brandFontFamily,
  "&.Mui-selected": {
    color: brandPrimaryColor,
    fontWeight: 400,
    borderBottom: `1px solid ${brandPrimaryColor}`,
  },
}));

// The customer counterpart of CompanySettings: same tab shell, driven by the
// `/customerSettings` route's childs. Reached from the avatar dropdown, and only
// by the account that owns the customer entity (see TopBar and routes.js).
const CustomerSettings = () => {
  const { isBannerVisible } = useContext(LayoutContext);
  const [value, setValue] = useState(0);
  const { authRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const customerSettingsRoute = routes.find((route) => route.path === "/customerSettings");

  const allTabs = customerSettingsRoute?.childs || [];
  const visibleTabs = allTabs.filter((tab) => tab?.roles?.includes(authRole));

  useEffect(() => {
    const currentTabIndex = visibleTabs?.findIndex((tab) =>
      location.pathname.endsWith(`/customerSettings/${tab.path}`),
    );

    if (currentTabIndex !== -1) {
      setValue(currentTabIndex);
    }
  }, [location.pathname, visibleTabs]);

  const handleChange = (event, newValue) => {
    const selectedTab = visibleTabs[newValue];

    navigate(`/customerSettings/${selectedTab.path}`);
    setValue(newValue);
  };

  return (
    <div className="bg-light h-full">
      <div
        className={`bg-light sm:fixed 2xl:w-[95.2%] xl:w-[93.2%] lg:w-[90.6%] md:w-[87.5%] sm:w-[95%] w-full h-screen transition-all ${
          isBannerVisible ? "top-[106px]" : "top-[56px]"
        }`}
      >
        <CustomerSettingsHeader />
        <BaseWrapper>
          <Tabs
            TabIndicatorProps={{ style: { display: "none" } }}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            value={value}
            onChange={handleChange}
            aria-label="customer settings tabs"
            sx={{
              "& .MuiTabs-flexContainer": { overflow: "visible !important" },
              "& .MuiTabs-scrollButtons.Mui-disabled": { width: 0, opacity: 0 },
            }}
          >
            {visibleTabs?.map((tab, index) => (
              <CustomTab
                key={index}
                label={tab.text}
                {...a11yProps(index)}
                sx={{ minHeight: "38px", marginRight: "40px" }}
              />
            ))}
          </Tabs>

          {visibleTabs?.map((tab, index) => (
            <CustomTabPanel value={value} index={index} key={index}>
              {tab.component}
            </CustomTabPanel>
          ))}
        </BaseWrapper>
      </div>
    </div>
  );
};

export default CustomerSettings;
